import { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, type MyMembership } from './api.js';

/**
 * WHAT THE PERSON LOOKING AT THIS PROJECT MAY DO.
 *
 * The SPA offered every action to everybody and let the server refuse. That was
 * tolerable while permissions were fixed and became a real defect the moment a
 * production could configure them: a producer sets a rule, and the screen
 * ignores it until somebody clicks and gets "that could not be saved".
 *
 * KEYED BY PROJECT, ALWAYS. One person is routinely a producer on their own
 * production and a location scout on somebody else's, and the two may have
 * redefined those roles differently — so there is no such thing as "this
 * user's permissions" to hold anywhere. The query key carries the project id
 * and the context is mounted per project, which is what stops the answer from
 * one production being shown inside another.
 */
const MembershipContext = createContext<MyMembership | null>(null);

export function ProjectPermissionsProvider({
  projectId,
  children,
}: {
  projectId: string;
  children: React.ReactNode;
}) {
  const membership = useQuery({
    queryKey: ['my-membership', projectId],
    queryFn: () => api.myMembership(projectId),
    // A permission change by somebody else should reach an open tab reasonably
    // soon without polling; a minute is far shorter than a production's
    // attention span and far longer than a click.
    staleTime: 60_000,
    retry: false,
  });

  const value = useMemo(() => membership.data ?? null, [membership.data]);

  return <MembershipContext.Provider value={value}>{children}</MembershipContext.Provider>;
}

/**
 * `null` while it is still loading or if the call failed.
 *
 * DELIBERATELY NOT `false`. A screen that treats "I do not know yet" as "you
 * may not" flickers every control on every navigation, and one that treats it
 * as "you may" is back to offering buttons the server will refuse. `can()`
 * below answers `true` for an unknown state and the surfaces explain the
 * refusal — which keeps the optimistic case working exactly as it did before
 * this existed, and the pessimistic case merely un-hinted.
 */
export function useMembership(): MyMembership | null {
  return useContext(MembershipContext);
}

/**
 * May they?
 *
 * Unknown answers YES on purpose: this is a HINT for the interface, never the
 * gate. The gate is `requirePermission` on the server and it does not consult
 * anything the browser says. A hint that guesses wrong shows a button that gets
 * refused, exactly as today; a hint that guesses wrong the other way hides a
 * control somebody is entitled to, and they have no way to find out why.
 */
export function useCan(permission: string): boolean {
  const membership = useMembership();
  if (membership === null) return true;
  return membership.permissions.includes(permission);
}

/**
 * "a" or "an", by how the role's first word is SAID, not spelt.
 *
 * Checked against every label in `ROLE_CATALOGUE` (28): "an" for Executive
 * Producer, Assistant Location Manager, Art Director and Observer; "a" for the
 * rest, including Unit Production Manager ("you-nit") and the 1st and 2nd
 * Assistant Director (said "first", "second"). A leading U said "you" takes "a".
 */
function indefiniteArticle(label: string): 'a' | 'an' {
  return /^(?:[aeio]|u(?!ni|s[eu]|ti))/i.test(label) ? 'an' : 'a';
}

/** The sentence `useRefusal` shows, for one role label. */
export function refusalSentence(label: string): string {
  return `Not something ${indefiniteArticle(label)} ${label} does on this production. A producer can change that under Team.`;
}

/** Why a control is unavailable, in words a producer can act on. */
export function useRefusal(permission: string): string | null {
  const membership = useMembership();
  if (membership === null) return null;
  if (membership.permissions.includes(permission)) return null;

  return refusalSentence(membership.label);
}
